import React, { useState, useEffect } from "react";
import { DataGrid, gridClasses } from "@mui/x-data-grid";
import {
  Menu,
  MenuItem,
  Paper,
  IconButton,
  TextField,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import AppsIcon from "@mui/icons-material/Apps";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";

import SuiBox from "components/SuiBox";
import SuiButton from "components/SuiButton";
import SuiTypography from "components/SuiTypography";

import TableTopControls from "common/TableTopControls";

import ApoderadosApi from "api/apoderadosApi";

import CreateApoderado from "./createApoderado";
import EditApoderado from "./editApoderado";

const ApoderadosTable = () => {
  const [apoderados, setApoderados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedApoderado, setSelectedApoderado] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const obtenerApoderados = async () => {
    try {
      const response = await ApoderadosApi.fetchApoderadosSoft();
      setApoderados(response.data);
    } catch (error) {
      console.error("Error al obtener apoderados:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    obtenerApoderados();
  }, []);

  const handleMenuClick = (event, id) => {
    setAnchorEl(event.currentTarget);
    setSelectedId(id);
    let apoderado = apoderados.find((a) => a.id === id);
    setSelectedApoderado(apoderado);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedId(null);
  };

  const handleEdit = (apoderado) => {
    setSelectedApoderado(apoderado);
    setEditModalOpen(true);
    handleMenuClose();
  };

  const handleDelete = async (id) => {
    if (window.confirm("¿Está seguro(a) de eliminar este apoderado?")) {
      try {
        await ApoderadosApi.deleteApoderado(id);
        obtenerApoderados();
      } catch (error) {
        console.error("Error al eliminar apoderado:", error);
      }
    }
    handleMenuClose();
  };

  const filteredApoderados = apoderados.filter((apoderado) => {
    const matchesSearch =
      apoderado.nombre_completo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(apoderado.identificacion).toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });
  const paginatedApoderados = filteredApoderados.slice(page * pageSize, (page + 1) * pageSize);

  const columns = [
    {
      field: "actions",
      renderHeader: () => <AppsIcon />,
      width: 50,
      sortable: false,
      align: "left",
      renderCell: (params) => (
        <IconButton onClick={(e) => handleMenuClick(e, params.row.id)} sx={{ padding: "4px" }}>
          <MoreVertIcon />
        </IconButton>
      ),
    },
    {
      field: "nombre",
      headerName: "Nombre Completo",
      flex: 2,
      renderCell: (params) => (
        <SuiTypography variant="caption" fontWeight="medium">
          {params.row.nombre_completo}
        </SuiTypography>
      ),
    },
    {
      field: "identificacion",
      headerName: "Identificación",
      flex: 1,
      renderCell: (params) => (
        <SuiTypography variant="caption">
          {params.row.tipo_identificacion} - {params.row.identificacion}
        </SuiTypography>
      ),
    },
    {
      field: "tarjeta_profesional",
      headerName: "Tarjeta Profesional",
      flex: 1,
      renderCell: (params) => (
        <SuiTypography variant="caption">{params.row.tarjeta_profesional}</SuiTypography>
      ),
    },
    {
      field: "celular",
      headerName: "Celular",
      flex: 1,
      renderCell: (params) => <SuiTypography variant="caption">{params.row.celular}</SuiTypography>,
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      renderCell: (params) => <SuiTypography variant="caption">{params.row.email}</SuiTypography>,
    },
  ];

  return (
    <SuiBox>
      <Paper
        sx={{
          width: "100%",
          overflow: "hidden",
          boxShadow: "none",
          backgroundColor: "transparent",
        }}
      >
        <SuiBox
          justifyContent="space-between"
          alignItems="center"
          p={1}
          sx={{
            borderBottom: ({ borders: { borderWidth, borderColor } }) =>
              `${borderWidth[1]} solid ${borderColor}`,
          }}
        >
          <Grid container columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={6}>
              <SuiBox display="flex" flexDirection="column">
                <strong>Administración de Apoderados</strong>
              </SuiBox>
            </Grid>
          </Grid>
          <SuiBox display="flex" alignItems="center" gap={1} sx={{ ml: 2 }}>
            <Tooltip title="Buscar por Nombre y Identificación" arrow>
              <TextField
                size="small"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(0);
                }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Tooltip>
            <TableTopControls
              page={page}
              onPageChange={setPage}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              totalRows={filteredApoderados.length}
            />
            <SuiButton
              variant="gradient"
              buttonColor="dark"
              size="small"
              onClick={() => {
                setSelectedApoderado(null);
                setShowCreateForm(true);
              }}
            >
              Nuevo Apoderado
            </SuiButton>
          </SuiBox>
        </SuiBox>

        <DataGrid
          rows={paginatedApoderados}
          columns={columns}
          loading={loading}
          getRowHeight={() => "auto"}
          sx={{
            border: 0,
            backgroundColor: "white",
            borderRadius: 2,
            p: 1,
            [`& .${gridClasses.cell}`]: {
              borderBottom: "none",
              padding: "2px 2px",
            },
            [`& .${gridClasses.row}`]: {
              backgroundColor: ({ palette: { white } }) => white.main,
              "&:hover": {
                backgroundColor: ({ palette: { grey } }) => grey[200],
              },
              minHeight: "24px !important",
            },
            [`& .${gridClasses.columnHeaders}`]: {
              backgroundColor: ({ palette: { grey } }) => grey[50],
              borderRadius: 1,
              minHeight: "24px !important",
            },
            [`& .${gridClasses.footerContainer}`]: {
              display: "none",
            },
          }}
          hideFooter
        />
      </Paper>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={() => handleEdit(apoderados.find((a) => a.id === selectedId))}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Editar
        </MenuItem>
        <MenuItem onClick={() => handleDelete(selectedId)} sx={{ color: "error.main" }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Eliminar
        </MenuItem>
      </Menu>

      <CreateApoderado
        open={showCreateForm}
        onClose={() => setShowCreateForm(false)}
        onSuccess={() => {
          obtenerApoderados();
          setShowCreateForm(false);
        }}
      />
      <EditApoderado
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        apoderado={selectedApoderado}
        onSuccess={() => {
          obtenerApoderados();
          setEditModalOpen(false);
        }}
      />
    </SuiBox>
  );
};

export default ApoderadosTable;
